import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { formateDate } from 'utils'
import Api from 'utils/api'

function PublicInvoiceView() {
  const { shareToken } = useParams()
  const [invoice, setInvoice] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchInvoice()
  }, [shareToken])

  const fetchInvoice = async () => {
    try {
      const { response, error: apiErr } = await Api(
        `/invoices/public/${shareToken}`,
        'get',
      )

      if (apiErr) {
        setError(apiErr.message || 'Invoice not found or sharing is disabled')
      } else {
        setInvoice(response.data)
      }
    } catch (err) {
      setError('Failed to load invoice')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className='tw-flex tw-justify-center tw-items-center tw-h-screen'>
        <div className='tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-[#1C274C]'></div>
      </div>
    )
  }

  if (error || !invoice) {
    return (
      <div className='tw-flex tw-justify-center tw-items-center tw-h-screen tw-px-4'>
        <div className='tw-bg-white tw-rounded-xl tw-shadow-lg tw-p-8 tw-text-center tw-max-w-md'>
          <p className='tw-text-red-600 tw-text-lg tw-font-medium'>
            {error || 'Invoice not found'}
          </p>
        </div>
      </div>
    )
  }

  const totalAmount =
    invoice.items?.length > 0
      ? invoice.items.reduce((sum, item) => sum + item.amount, 0)
      : invoice.amountDue

  const getStatusColor = (status) => {
    const colors = {
      draft: 'tw-bg-gray-100 tw-text-gray-800',
      due: 'tw-bg-blue-100 tw-text-blue-800',
      overdue: 'tw-bg-red-100 tw-text-red-800',
      paid: 'tw-bg-green-100 tw-text-green-800',
      cancelled: 'tw-bg-gray-100 tw-text-gray-800',
    }
    return colors[status] || 'tw-bg-gray-100 tw-text-gray-800'
  }

  return (
    <div className='tw-min-h-screen tw-bg-gradient-to-br tw-from-gray-50 tw-to-gray-100 tw-p-4 tw-py-8'>
      <div className='tw-max-w-4xl tw-mx-auto'>
        {/* Header with Print Button */}
        <div className='tw-flex tw-justify-between tw-items-center tw-mb-6'>
          <h1 className='tw-text-3xl tw-font-bold tw-text-[#1C274C]'>
            Invoice
          </h1>
          <button
            onClick={() => window.print()}
            className='tw-px-6 tw-py-2 tw-bg-[#1C274C] tw-text-white tw-rounded-lg hover:tw-bg-[#2c3a6d] tw-font-medium'
          >
            🖨️ Print / Save PDF
          </button>
        </div>

        {/* Invoice Content */}
        <div className='tw-bg-white tw-rounded-xl tw-shadow-lg tw-p-8 md:tw-p-12'>
          {/* Invoice Header */}
          <div className='tw-grid tw-grid-cols-2 tw-gap-8 tw-mb-12 tw-pb-8 tw-border-b tw-border-gray-200'>
            <div>
              <h2 className='tw-text-sm tw-font-medium tw-text-gray-500 tw-uppercase tw-mb-2'>
                From
              </h2>
              <p className='tw-text-lg tw-font-semibold tw-text-gray-900'>
                Devtronics
              </p>
              <p className='tw-text-sm tw-text-gray-600'>
                Digital Business Cards
              </p>
            </div>
            <div className='tw-text-right'>
              <p className='tw-text-sm tw-font-medium tw-text-gray-500 tw-uppercase tw-mb-2'>
                Invoice #
              </p>
              <p className='tw-text-2xl tw-font-bold tw-text-[#1C274C]'>
                {invoice.invoiceNumber}
              </p>
              <p className='tw-text-sm tw-text-gray-600 tw-mt-4'>
                Issued: {formateDate(invoice.issueDate)}
              </p>
            </div>
          </div>

          {/* Bill To */}
          <div className='tw-grid tw-grid-cols-2 tw-gap-8 tw-mb-12'>
            <div>
              <h3 className='tw-text-sm tw-font-medium tw-text-gray-500 tw-uppercase tw-mb-3'>
                Bill To
              </h3>
              <div className='tw-space-y-1'>
                <p className='tw-text-gray-900 tw-font-medium'>Customer ID</p>
                <p className='tw-text-gray-600 tw-text-sm'>
                  {invoice.customerId}
                </p>
              </div>
            </div>
            <div>
              <h3 className='tw-text-sm tw-font-medium tw-text-gray-500 tw-uppercase tw-mb-3'>
                Invoice Details
              </h3>
              <div className='tw-space-y-2'>
                <div className='tw-flex tw-justify-between tw-text-sm'>
                  <span className='tw-text-gray-600'>Due Date:</span>
                  <span className='tw-font-medium tw-text-gray-900'>
                    {formateDate(invoice.dueDate)}
                  </span>
                </div>
                <div className='tw-flex tw-justify-between tw-text-sm'>
                  <span className='tw-text-gray-600'>Status:</span>
                  <span
                    className={`tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-medium ${getStatusColor(invoice.status)}`}
                  >
                    {invoice.status.toUpperCase()}
                  </span>
                </div>
                {invoice.billingPeriod && (
                  <div className='tw-flex tw-justify-between tw-text-sm'>
                    <span className='tw-text-gray-600'>Period:</span>
                    <span className='tw-font-medium tw-text-gray-900'>
                      {invoice.billingPeriod}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Line Items */}
          {invoice.items && invoice.items.length > 0 ? (
            <div className='tw-mb-12'>
              <table className='tw-w-full tw-text-sm'>
                <thead className='tw-bg-gray-50 tw-border-t tw-border-b tw-border-gray-200'>
                  <tr>
                    <th className='tw-text-left tw-px-4 tw-py-3 tw-font-semibold tw-text-gray-900'>
                      Description
                    </th>
                    <th className='tw-text-right tw-px-4 tw-py-3 tw-font-semibold tw-text-gray-900 tw-w-20'>
                      Qty
                    </th>
                    <th className='tw-text-right tw-px-4 tw-py-3 tw-font-semibold tw-text-gray-900 tw-w-24'>
                      Unit Price
                    </th>
                    <th className='tw-text-right tw-px-4 tw-py-3 tw-font-semibold tw-text-gray-900 tw-w-24'>
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items.map((item, index) => (
                    <tr key={index} className='tw-border-b tw-border-gray-100'>
                      <td className='tw-px-4 tw-py-3 tw-text-gray-900'>
                        {item.description}
                      </td>
                      <td className='tw-text-right tw-px-4 tw-py-3 tw-text-gray-600'>
                        {item.quantity}
                      </td>
                      <td className='tw-text-right tw-px-4 tw-py-3 tw-text-gray-600'>
                        ${item.unitPrice.toFixed(2)}
                      </td>
                      <td className='tw-text-right tw-px-4 tw-py-3 tw-text-gray-900 tw-font-medium'>
                        ${item.amount.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className='tw-mb-12 tw-p-4 tw-bg-gray-50 tw-rounded-lg tw-text-center'>
              <p className='tw-text-gray-600 tw-text-sm'>
                {invoice.description || 'No line items'}
              </p>
            </div>
          )}

          {/* Summary */}
          <div className='tw-flex tw-justify-end tw-mb-12'>
            <div className='tw-w-full sm:tw-w-80'>
              <div className='tw-space-y-2 tw-p-4 tw-bg-gray-50 tw-rounded-lg'>
                {invoice.items && invoice.items.length > 0 && (
                  <div className='tw-flex tw-justify-between tw-text-sm'>
                    <span className='tw-text-gray-600'>Subtotal:</span>
                    <span className='tw-text-gray-900'>
                      ${totalAmount.toFixed(2)}
                    </span>
                  </div>
                )}
                <div className='tw-flex tw-justify-between tw-text-lg tw-font-bold tw-pt-2 tw-border-t tw-border-gray-200'>
                  <span className='tw-text-gray-900'>Total Due:</span>
                  <span className='tw-text-[#1C274C]'>
                    ${invoice.amountDue.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Status Section */}
          {invoice.status === 'paid' && (
            <div className='tw-p-4 tw-bg-green-50 tw-border tw-border-green-200 tw-rounded-lg tw-mb-8'>
              <p className='tw-text-green-800 tw-font-medium'>
                ✓ This invoice has been paid
              </p>
              {invoice.paidDate && (
                <p className='tw-text-sm tw-text-green-700 tw-mt-1'>
                  Paid on {formateDate(invoice.paidDate)}
                  {invoice.paymentMethod && ` via ${invoice.paymentMethod}`}
                </p>
              )}
            </div>
          )}

          {invoice.status === 'overdue' && (
            <div className='tw-p-4 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-mb-8'>
              <p className='tw-text-red-800 tw-font-medium'>
                ⚠️ This invoice is overdue
              </p>
              <p className='tw-text-sm tw-text-red-700 tw-mt-1'>
                Due date was {formateDate(invoice.dueDate)}
              </p>
            </div>
          )}

          {/* Notes */}
          {invoice.paymentNotes && (
            <div className='tw-p-4 tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-mb-8'>
              <h4 className='tw-text-sm tw-font-semibold tw-text-blue-900 tw-mb-2'>
                Notes
              </h4>
              <p className='tw-text-sm tw-text-blue-800'>
                {invoice.paymentNotes}
              </p>
            </div>
          )}

          {/* Footer */}
          <div className='tw-pt-8 tw-border-t tw-border-gray-200 tw-text-center tw-text-sm tw-text-gray-500'>
            <p>Thank you for your business!</p>
            <p className='tw-mt-2'>
              For inquiries, please contact us at contact@loyalideas.com
            </p>
            <p className='tw-mt-4 tw-text-xs'>
              Generated on {formateDate(new Date())} | Share Token:{' '}
              {shareToken.substring(0, 8)}...
            </p>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            background: white;
          }
          button {
            display: none;
          }
          .tw-shadow-lg {
            box-shadow: none;
          }
        }
      `}</style>
    </div>
  )
}

export default PublicInvoiceView
